<template>
    <div class="vp-bottom">
        <media-time-range class="vp-time-range">
            <div slot="preview" class="vp-preview vp-panel">
                <div v-if="hasThumbnails" class="vp-preview-frame">
                    <media-preview-thumbnail class="vp-preview-thumbnail"></media-preview-thumbnail>
                </div>
                <div class="vp-preview-meta">
                    <media-preview-chapter-display class="vp-preview-chapter"></media-preview-chapter-display>
                    <media-preview-time-display class="vp-preview-time"></media-preview-time-display>
                </div>
            </div>
        </media-time-range>

        <media-control-bar class="vp-control-row">
            <media-play-button class="vp-media-button">
                <VideoIcon slot="play" name="play" :size="22" />
                <VideoIcon slot="pause" name="pause" :size="22" />
            </media-play-button>

            <media-mute-button class="vp-media-button">
                <VideoIcon v-for="level in ['high', 'medium', 'low']" :key="level" :slot="level" name="volume" />
                <VideoIcon slot="off" name="muted" />
            </media-mute-button>

            <media-volume-range class="vp-volume"></media-volume-range>

            <span class="vp-time vp-mono">
                <media-time-display class="vp-time-current"></media-time-display>
                <span class="vp-time-total">&nbsp;/&nbsp;<media-duration-display></media-duration-display></span>
            </span>

            <span v-if="chapterLabel" class="vp-chapter-label">{{ chapterLabel }}</span>

            <span class="vp-spacer"></span>

            <media-playback-rate-button class="vp-rate" rates="1 1.25 1.5 1.75 2"></media-playback-rate-button>

            <media-captions-button v-if="hasSubtitles" class="vp-media-button vp-captions">
                <VideoIcon slot="on" name="captions" />
                <VideoIcon slot="off" name="captions" />
            </media-captions-button>

            <button
                v-if="hasChapters"
                type="button"
                class="vp-icon-button"
                data-vp-chapters-toggle
                :aria-label="t('Chapters')"
                :aria-expanded="chaptersOpen"
                @click="emit('toggle-chapters')"
            >
                <VideoIcon name="chapters" />
            </button>

            <VideoQualityMenu v-if="qualityVisible" :levels="levels" :selected="selectedLevel" @select="(index) => emit('select-quality', index)" />

            <media-pip-button v-if="videoControls" class="vp-media-button">
                <VideoIcon slot="enter" name="pip-enter" />
                <VideoIcon slot="exit" name="pip-exit" />
            </media-pip-button>

            <media-fullscreen-button v-if="videoControls" class="vp-media-button">
                <VideoIcon slot="enter" name="fullscreen-enter" />
                <VideoIcon slot="exit" name="fullscreen-exit" />
            </media-fullscreen-button>
        </media-control-bar>
    </div>
</template>

<script setup>
import "media-chrome/media-time-range.js"
import "media-chrome/media-control-bar.js"
import "media-chrome/media-play-button.js"
import "media-chrome/media-mute-button.js"
import "media-chrome/media-volume-range.js"
import "media-chrome/media-time-display.js"
import "media-chrome/media-duration-display.js"
import "media-chrome/media-playback-rate-button.js"
import "media-chrome/media-captions-button.js"
import "media-chrome/media-pip-button.js"
import "media-chrome/media-fullscreen-button.js"
import "media-chrome/media-preview-thumbnail.js"
import "media-chrome/media-preview-chapter-display.js"
import "media-chrome/media-preview-time-display.js"
import VideoIcon from "./VideoIcon.vue"
import VideoQualityMenu from "./VideoQualityMenu.vue"
import { useCoreI18n } from "../useCoreI18n.js"

defineProps({
    chapterLabel: { type: String, default: null },
    hasChapters: { type: Boolean, default: false },
    chaptersOpen: { type: Boolean, default: false },
    hasThumbnails: { type: Boolean, default: false },
    hasSubtitles: { type: Boolean, default: false },
    levels: { type: Array, default: () => [] },
    qualityVisible: { type: Boolean, default: false },
    selectedLevel: { type: Number, default: -1 },
    videoControls: { type: Boolean, default: true },
})

const emit = defineEmits(["toggle-chapters", "select-quality"])

const t = useCoreI18n()
</script>

<style>
.vp-bottom {
    position: relative;
    z-index: 1;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 56px 24px 18px;
    background: linear-gradient(0deg, rgba(21, 18, 13, 0.92) 0%, rgba(21, 18, 13, 0.58) 48%, rgba(21, 18, 13, 0) 100%);
}

.vp-time-range {
    display: block;
    width: 100%;
    height: 20px;
    --media-range-padding: 0;
    --media-range-track-height: 6px;
    --media-range-track-border-radius: 3px;
    --media-range-track-background: rgba(207, 200, 188, 0.26);
    --media-time-range-buffered-color: rgba(207, 200, 188, 0.44);
    --media-range-bar-color: var(--vp-accent);
    --media-range-segments-gap: 3px;
    --media-range-thumb-width: 14px;
    --media-range-thumb-height: 14px;
    --media-range-thumb-background: var(--vp-paper);
    --media-range-thumb-box-shadow: 0 0 0 3px rgba(21, 18, 13, 0.4);
    --media-preview-background: transparent;
    --media-box-padding-left: 0px;
    --media-box-padding-right: 0px;
    --media-box-arrow-display: none;
    --media-preview-chapter-background: transparent;
    --media-preview-time-background: transparent;
}

.vp-preview {
    width: 208px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    background: rgba(21, 18, 13, 0.92);
}

.vp-preview-frame {
    width: 192px;
    height: 108px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 8px;
    background: rgba(251, 249, 245, 0.06);
}

/* media-chrome reads the real max-* properties (not custom properties) to scale the cue down. */
.vp-preview-thumbnail {
    min-width: 192px;
    max-width: 192px;
    min-height: 108px;
    max-height: 108px;
}

.vp-preview-meta {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 4px 2px;
}

.vp-preview-chapter {
    min-width: 0;
    --media-control-padding: 0;
    --media-font-size: 12px;
    --media-font-weight: 500;
    --media-text-color: var(--vp-paper);
}

.vp-preview-time {
    --media-control-padding: 0;
    --media-font-family: "JetBrains Mono", ui-monospace, monospace;
    --media-font-size: 12px;
    --media-text-color: var(--vp-muted);
}

.vp-control-row {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4px;
    --media-control-padding: 0;
}

.vp-media-button {
    width: 44px;
    height: 44px;
    padding: 0;
    border-radius: 10px;
    --media-button-icon-width: 21px;
    --media-button-icon-height: 21px;
}

.vp-volume {
    width: 78px;
    --media-range-padding: 0;
    --media-range-track-height: 4px;
    --media-range-track-border-radius: 2px;
    --media-range-track-background: rgba(207, 200, 188, 0.26);
    --media-range-bar-color: var(--vp-muted);
    --media-range-thumb-width: 12px;
    --media-range-thumb-height: 12px;
    --media-range-thumb-background: var(--vp-paper);
}

.vp-time {
    display: inline-flex;
    align-items: center;
    margin-left: 14px;
    font-size: 12.5px;
    --media-control-padding: 0;
    --media-font-family: "JetBrains Mono", ui-monospace, monospace;
    --media-font-size: 12.5px;
}

.vp-time-total {
    display: inline-flex;
    align-items: center;
    color: rgba(207, 200, 188, 0.72);
    --media-text-color: rgba(207, 200, 188, 0.72);
}

.vp-chapter-label {
    min-width: 0;
    margin-left: 12px;
    overflow: hidden;
    font-size: 13px;
    color: var(--vp-muted);
    white-space: nowrap;
    text-overflow: ellipsis;
}

.vp-rate {
    height: 32px;
    padding: 0 11px;
    border: 1px solid rgba(251, 249, 245, 0.24);
    border-radius: 999px;
    --media-font-family: "JetBrains Mono", ui-monospace, monospace;
    --media-font-size: 12px;
    --media-font-weight: 500;
}

.vp-captions[mediasubtitlesshowing] {
    position: relative;
    background: rgba(251, 249, 245, 0.14);
}

.vp-captions[mediasubtitlesshowing]::after {
    content: "";
    position: absolute;
    bottom: 5px;
    left: 50%;
    width: 16px;
    height: 2px;
    margin-left: -8px;
    border-radius: 2px;
    background: var(--vp-caption-on);
}

media-controller:not([breakpointmd]) .vp-volume,
media-controller:not([breakpointmd]) .vp-chapter-label,
media-controller:not([breakpointsm]) .vp-rate {
    display: none;
}
</style>
